// LINHA DISCRETA DE SEO dos rodapés dos modelos 7–11 (Aurora 7 · Horizonte 8 · Atelier 9 · Panorama 10 ·
// Larissa 11): os links de cidades, tipos e empreendimentos que o rodapé CLÁSSICO (about1/AboutPieces1:
// "IMÓVEIS POR CIDADE", "IMÓVEIS POR TIPO", "EMPREENDIMENTOS") sempre teve e os rodapés novos não tinham
// (Daniel 16/10: "faça o item 2 com a linha discreta no rodapé").
//  - MESMOS dados, ordem, limite e montagem de URL do AboutPieces1 (inclusive os gates 2558/2146/1884/2474);
//  - só entra o que o rodapé AINDA não mostra (`jaTem`: hrefs dos links que a peça já renderiza) — a
//    comparação é pela entidade (cidade/tipo/empreendimento), sem duplicar link no mesmo rodapé;
//  - texto corrido "Rótulo: a · b · c", fonte pequena, cor = a do texto do rodapé (herdada) mais suave;
//    grupo vazio não renderiza; nada para mostrar = nada no HTML.
import { hifenUrl } from '../../../utils/hifenUrl';
import Link from 'next/link';
import React from 'react';
import { capitalizeWords, formatSelects, removerAcentos } from '../../../utils/FunctionsGlobals';
import { trackMetaEvent } from '../../../utils/metaPixel';
import styles from './LinhaSeoRodape.module.css';

type ItemSeo = { label: string; href: string; title: string; cat: string };
type GrupoSeo = { rotulo: string; itens: ItemSeo[] };

interface ILinhaSeoRodape {
    site: any;
    allCities?: any;
    allCitiesTipes?: any;
    allRodape?: any;
    /** o withCodsite do próprio rodapé (anexa ?codsite= só em host de teste, depois de montar). */
    withCodsite: (href: string) => string;
    /** hrefs dos links de listagem que o rodapé já mostra (marquee/"Buscas populares"/menus). */
    jaTem?: Array<string | undefined | null>;
    className?: string;
}

const normaliza = (v: string): string => {
    let d = v;
    try {
        d = decodeURIComponent(v);
    } catch {
        // segmento com % solto: compara cru
    }
    return removerAcentos(d).toLowerCase().replace(/002d/g, '-').replace(/[\s-]+/g, '-').replace(/^-+|-+$/g, '');
};

/** "c:<cidade>" | "t:<tipo>" | "e:<empreendimento>" de um link /imoveis/<transação>/<cidade>/<bairro>/<condomínio>/<tipo>
 * com UM só filtro preenchido (ignora transação, hífen × espaço, acento e o escape 002d); demais links = null. */
export const chaveDaListagem = (href?: string | null): string | null => {
    const seg = String(href || '').split('#')[0].split('?')[0].split('/');
    if (seg[1] !== 'imoveis' || seg.length < 7) return null;
    const [cidade, bairro, condominio, tipo] = seg.slice(3, 7).map(normaliza);
    const vazio = (v: string) => !v;
    if (!vazio(cidade) && vazio(bairro) && vazio(condominio) && vazio(tipo)) return `c:${cidade}`;
    if (vazio(cidade) && vazio(bairro) && vazio(condominio) && !vazio(tipo)) return `t:${tipo}`;
    if (vazio(cidade) && vazio(bairro) && !vazio(condominio) && vazio(tipo)) return `e:${condominio}`;
    return null;
};

/** Os 3 grupos do rodapé clássico (AboutPieces1), na mesma ordem/limite/URL. */
export const gruposSeoDoClassico = (site: any, allCities: any, allCitiesTipes: any, allRodape: any): GrupoSeo[] => {
    const transacao = String(site?.transacoes?.[0] || 'venda').toLowerCase();

    // IMÓVEIS POR CIDADE: as 10 primeiras de allCities (as sem nome não aparecem)
    const cidades: ItemSeo[] = (Array.isArray(allCities) ? allCities : [])
        .slice(0, 10)
        .filter((v: any) => v?.label)
        .map((v: any) => ({
            label: String(v.label),
            href: `/imoveis/${transacao}/${removerAcentos(v.label).toLowerCase()}/-/-/-`.replaceAll(' ', '-'),
            title: `Imóveis em ${v.label}`,
            cat: 'footer_city',
        }));

    // IMÓVEIS POR TIPO: os 10 primeiros tipos (formatSelects), sempre em /venda — exceto cs2558; cs2146 + Studio
    const tipos: ItemSeo[] =
        site?.id != 2558
            ? (formatSelects(allCitiesTipes) || []).slice(0, 10).map((e: any) => ({
                  label: String(e?.label),
                  href: `/imoveis/venda/-/-/-/${removerAcentos((e?.label ? hifenUrl(e.label, site?.id) : e?.label)?.replaceAll(' ', '-')).toLowerCase()}`,
                  title: `${e?.label} em ${site?.Dados?.cidade} e região`,
                  cat: 'footer_property_type',
              }))
            : [];
    if (site?.id == 2146) tipos.push({ label: 'Studio', href: '/imoveis/venda/-/-/-/Studio', title: `Studio em ${site?.Dados?.cidade} e região`, cat: 'footer_property_type' });

    // EMPREENDIMENTOS: com exibir_empreendimento, os 10 primeiros distintos de allRodape
    const empreendimentos: ItemSeo[] = site?.exibir_empreendimento
        ? (Array.isArray(allRodape) ? allRodape : [])
              .filter((v: any) => v?.empreendimento)
              .filter((v: any, i: number, todos: any[]) => todos.findIndex((x) => x.empreendimento === v.empreendimento) === i)
              .slice(0, 10)
              .map((v: any) => ({
                  label: capitalizeWords(String(v.empreendimento).toLowerCase()),
                  href: `/imoveis/${transacao}/-/-/${removerAcentos(v.empreendimento).toLowerCase()?.replaceAll('\n', '-')}/-`.replaceAll(' ', '-'),
                  title: `Empreendimento ${v.empreendimento}`,
                  cat: 'footer_development',
              }))
        : [];
    const rotuloEmpreendimentos = site?.id == 1884 ? 'Residenciais' : site?.id == 2474 ? 'Ofertas em condomínio' : 'Empreendimentos';

    return [
        { rotulo: 'Imóveis em', itens: cidades },
        { rotulo: 'Tipos', itens: tipos },
        { rotulo: rotuloEmpreendimentos, itens: empreendimentos },
    ];
};

const LinhaSeoRodape: React.FC<ILinhaSeoRodape> = ({ site, allCities, allCitiesTipes, allRodape, withCodsite, jaTem = [], className }) => {
    const vistos = new Set(jaTem.map((h) => chaveDaListagem(h)).filter(Boolean) as string[]);
    const grupos = gruposSeoDoClassico(site, allCities, allCitiesTipes, allRodape)
        .map((g) => ({
            ...g,
            itens: g.itens.filter((it) => {
                const chave = chaveDaListagem(it.href);
                if (!chave || vistos.has(chave)) return false;
                vistos.add(chave);
                return true;
            }),
        }))
        .filter((g) => g.itens.length > 0);
    if (!grupos.length) return null;

    return (
        <nav className={`${styles.linha}${className ? ` ${className}` : ''}`} aria-label="Imóveis por cidade, tipo e empreendimento" data-el="rodape-seo">
            {grupos.map((g) => (
                <p key={g.rotulo} className={styles.grupo}>
                    <span className={styles.rotulo}>{g.rotulo}:</span>{' '}
                    {g.itens.map((it, idx) => (
                        <React.Fragment key={it.href}>
                            {idx > 0 ? <span className={styles.separador} aria-hidden="true"> · </span> : null}
                            <Link
                                prefetch={false}
                                href={withCodsite(it.href)}
                                title={it.title}
                                className={styles.link}
                                onClick={() => trackMetaEvent(site, 'ViewContent', { content_name: it.label, content_category: it.cat })}
                            >
                                {it.label}
                            </Link>
                        </React.Fragment>
                    ))}
                </p>
            ))}
        </nav>
    );
};

export default LinhaSeoRodape;
